import { Component, type ReactNode } from "react";
import {
  canReloadForStaleChunk,
  isStaleChunkReloadInFlight,
  reloadOnceForStaleChunk,
} from "./stale-chunk";

interface IProps {
  children: ReactNode;
}

interface IState {
  hasError: boolean;
  /** Decided before the first error render so the fallback never flashes. */
  willReload: boolean;
}

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload|ChunkLoadError/i;

export class ErrorBoundary extends Component<IProps, IState> {
  state: IState = { hasError: false, willReload: false };

  static getDerivedStateFromError(error: Error): IState {
    // Only a missing lazy chunk (stale tab after a deploy) auto-reloads, once.
    // Every other crash, or a repeat chunk failure, shows the fallback screen.
    const isChunkError = CHUNK_ERROR_PATTERN.test(error?.message ?? "");
    // A reload may already be in flight (started by the vite:preloadError handler, which
    // also stamps the guard), so the guard alone would wrongly say "no reload".
    return {
      hasError: true,
      willReload:
        isStaleChunkReloadInFlight() ||
        (isChunkError && canReloadForStaleChunk()),
    };
  }

  componentDidCatch() {
    if (this.state.willReload) reloadOnceForStaleChunk();
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    // Reload is in flight: render nothing instead of flashing the error screen.
    if (this.state.willReload) return null;

    return (
      <div className="flex h-screen flex-col items-center justify-center gap-16 px-16 text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-sm text-gray-600">
          The page failed to load. Please reload to try again.
        </p>
        <button
          type="button"
          className="rounded-lg bg-blue-600 px-24 py-8 text-white"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}
